import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Hero from '../components/Hero';
import Row from '../components/Row';
import MovieModal from '../components/MovieModal';
import AdSlot from '../components/AdSlot';
import SponsorshipBanner from '../components/SponsorshipBanner';
import AdvertiseWithUs from '../components/AdvertiseWithUs';
import Footer from '../components/Footer';
import PUBLIC_DOMAIN_MOVIES from '../data/publicDomainMovies';
import { endpoints, IMG_BASE, IMG_W500 } from '../utils/tmdb';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const normalize = (items) =>
  items.map((i) => ({
    id: i.item_id,
    media_type: i.media_type,
    title: i.title,
    name: i.title,
    backdrop_path: i.backdrop_path,
    poster_path: i.poster_path,
    overview: i.overview,
    progress: i.progress,
    first_air_date: i.media_type === 'tv' ? '2020-01-01' : undefined,
  }));

const ContinueWatchingRow = ({ items, onSelect, onRemove }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="pl-4 md:pl-14 mt-8">
      <h2 className="text-white text-lg md:text-2xl font-semibold mb-3">Continue Watching</h2>
      <div className="flex gap-2 overflow-x-scroll scrollbar-hide scroll-smooth pr-4 md:pr-14 py-6">
        {items.map((it) => {
          let img = '';
          if (it.backdrop_path) img = `${IMG_BASE}${it.backdrop_path}`;
          else if (it.poster_path) img = `${IMG_W500}${it.poster_path}`;
          const progressPct = Math.max(4, (it.progress || 0.1) * 100);
          return (
            <div
              key={it.id}
              onClick={() => onSelect(it)}
              className="relative flex-shrink-0 w-[240px] md:w-[300px] cursor-pointer rounded overflow-hidden transition-transform duration-300 hover:scale-105 hover:z-30 group"
            >
              {img ? (
                <img src={img} alt={it.title} className="w-full h-[135px] md:h-[170px] object-cover" loading="lazy" />
              ) : (
                <div className="w-full h-[135px] md:h-[170px] bg-white/10" />
              )}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-2 pt-6">
                <div className="text-white text-xs md:text-sm font-medium truncate">{it.title}</div>
                <div className="h-1 bg-white/20 rounded mt-1.5 overflow-hidden">
                  <div className="h-full bg-[#e11d48]" style={{ width: `${progressPct}%` }} />
                </div>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); onRemove(it); }}
                className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 bg-black/70 text-white text-xs px-2 py-1 rounded transition-opacity"
              >
                Remove
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Home = () => {
  const { currentProfile } = useAuth();
  const [selected, setSelected] = useState(null);
  const [mylist, setMylist] = useState([]);
  const [continueWatching, setContinueWatching] = useState([]);
  const [ytMovies, setYtMovies] = useState([]);

  const isKid = !!currentProfile?.is_kid;

  const refreshMyList = React.useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/mylist`);
      setMylist(data.items || []);
    } catch (e) {
      console.error('[Home] Failed to load My List:', e);
      setMylist([]);
    }
  }, []);

  const refreshContinue = React.useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/continue-watching`);
      setContinueWatching(data.items || []);
    } catch (e) {
      console.error('[Home] Failed to load Continue Watching:', e);
      setContinueWatching([]);
    }
  }, []);

  useEffect(() => {
    if (!currentProfile) return;
    refreshMyList();
    refreshContinue();
    (async () => {
      try {
        const { data } = await axios.get(`${API}/youtube-movies`);
        setYtMovies(
          (data.movies || []).map((m) => ({
            id: `yt_${m.id}`,
            media_type: 'movie',
            title: m.title,
            overview: m.overview || '',
            backdrop_path: m.backdrop_url,
            poster_path: m.poster_url,
            youtube_id: m.youtube_id,
            release_date: m.year ? `${m.year}-01-01` : undefined,
            runtime: m.runtime_min,
            vote_average: 7.5,
            genres_label: m.genres_label,
          }))
        );
      } catch (e) {
        console.error('[Home] Failed to load YouTube movies:', e);
        setYtMovies([]);
      }
    })();
  }, [currentProfile, refreshMyList, refreshContinue]);

  const removeContinue = async (it) => {
    try {
      await axios.delete(`${API}/continue-watching/${it.media_type}/${it.id}`);
      setContinueWatching((prev) => prev.filter((x) => x.item_id !== it.id));
    } catch (e) {
      console.error('[Home] Failed to remove continue-watching item:', e);
    }
  };

  const heroPath = isKid ? endpoints.animationMovies : endpoints.netflixOriginals;

  return (
    <div className="bg-[#141414] min-h-screen">
      <Navbar />
      <Hero onMoreInfo={setSelected} fetchPath={heroPath} />
      <div className="-mt-24 relative z-10">
        <SponsorshipBanner placement="home_top" />

        <ContinueWatchingRow
          items={normalize(continueWatching)}
          onSelect={setSelected}
          onRemove={removeContinue}
        />

        {mylist.length > 0 && (
          <Row title="My List" items={normalize(mylist)} onSelect={setSelected} />
        )}

        {isKid ? (
          <>
            <Row title="Kids' Favorites" fetchPath={endpoints.kidsFamily} onSelect={setSelected} />
            <Row title="Animated Adventures" fetchPath={endpoints.animationMovies} onSelect={setSelected} />
            <AdSlot slotId="1234567891" className="px-4 md:px-14 mt-8" />
            <Row title="Kids TV" fetchPath={endpoints.kidsShows} onSelect={setSelected} />
            <SponsorshipBanner placement="kids_top" className="mt-8" />
            <Row title="Family Movies" fetchPath={endpoints.familyMovies} onSelect={setSelected} />
          </>
        ) : (
          <>
            <Row title="Trending Now" fetchPath={endpoints.trending} onSelect={setSelected} />
            <Row title="🎬 Watch Free — Full Movies (2005–2019)" items={PUBLIC_DOMAIN_MOVIES} onSelect={setSelected} />
            {ytMovies.length > 0 && (
              <Row title="📺 More Free Full Movies (Curated)" items={ytMovies} onSelect={setSelected} />
            )}
            <Row title="NovaReelTV Originals" fetchPath={endpoints.netflixOriginals} isLarge onSelect={setSelected} />
            <AdSlot slotId="1234567890" className="px-4 md:px-14 mt-8" />
            <Row title="New & Top Rated" fetchPath={endpoints.topRated} onSelect={setSelected} />
            <Row title="Action Thrillers" fetchPath={endpoints.actionMovies} onSelect={setSelected} />
            <SponsorshipBanner placement="home_middle" className="mt-8" />
            <Row title="Popular on NovaReelTV" fetchPath={endpoints.popularTv} onSelect={setSelected} />
            <Row title="Comedies" fetchPath={endpoints.comedyMovies} onSelect={setSelected} />
            <div className="px-4 md:px-14 mt-8">
              <AdvertiseWithUs variant="card" />
            </div>
            <AdSlot slotId="1234567892" className="px-4 md:px-14 mt-8" />
            <Row title="Sci-Fi" fetchPath={endpoints.scifiMovies} onSelect={setSelected} />
            <Row title="Romance" fetchPath={endpoints.romanceMovies} onSelect={setSelected} />
            <Row title="Horror" fetchPath={endpoints.horrorMovies} onSelect={setSelected} />
            <Row title="Documentaries" fetchPath={endpoints.documentaries} onSelect={setSelected} />
          </>
        )}
      </div>
      <Footer />
      <MovieModal
        item={selected}
        onClose={() => setSelected(null)}
        onListChanged={refreshMyList}
        onContinueChanged={refreshContinue}
        allowRent={false}
      />
    </div>
  );
};

export default Home;
